// Meta-employee email eligibility (console mirror of the backend single source
// of truth `apps/api/src/dreamwright_api/email_domain.py::is_meta_email`).
//
// This is a UX gate ONLY — the authoritative enforcement is server-side at the
// project-config PATCH and audio-split submit boundaries. The console uses it to
// hide the Enhanced audio-isolation selector from non-Meta users. Kept as a tiny
// dependency-free helper so it can be unit-tested in isolation and reused by any
// future Meta-only surface without dragging in auth/react.

// Canonical Meta-employee email domain. Enhanced is gated on EXACTLY this domain
// (subdomain-strict, suffix-attack-safe) — deliberately independent of the
// configurable backend auth allowlist.
export const META_EMAIL_DOMAIN = 'meta.com'

/**
 * Whether `email` is a boundary-safe, case-insensitive exact `@meta.com` address.
 *
 * Mirrors the hardened backend parse so both layers agree:
 * - case-insensitive (`USER@META.COM` -> true);
 * - subdomain-strict (`user@corp.meta.com` -> false);
 * - suffix-attack-safe (`user@meta.com.evil.com`, `user@evil-meta.com`,
 *   `user@notmeta.com` -> false);
 * - missing / malformed (`null`, `undefined`, `''`, `'@meta.com'`,
 *   `'user@@meta.com'`) -> false.
 */
export function isMetaEmail(email: string | null | undefined): boolean {
  if (!email) return false
  const addr = email.trim().toLowerCase()
  const parts = addr.split('@')
  // Must have exactly one @ and a non-empty local part and domain.
  if (parts.length !== 2 || !parts[0] || !parts[1]) return false
  return parts[1] === META_EMAIL_DOMAIN
}

/**
 * Local auth-disabled development bypass for Meta-only UI.
 *
 * `import.meta.env.DEV` is compile-time false in production builds, so deployed
 * consoles cannot activate this by runtime config or hostname spoofing.
 */
export function isLocalMetaUiBypass(): boolean {
  return (
    import.meta.env.DEV &&
    !import.meta.env.VITE_ENABLE_AUTH &&
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  )
}

export function canSeeMetaOnlyUi(
  user: { email?: string | null } | null | undefined,
  localBypass: boolean = isLocalMetaUiBypass(),
): boolean {
  if (isMetaEmail(user?.email)) return true
  return localBypass && (user == null || user.email == null || user.email === 'debug@localhost')
}
